import { Component, type ErrorInfo, type ReactNode } from 'react';

import { Sentry } from '@/lib/monitoring/sentry';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
}

// App-wide error boundary. Reports to Sentry (a no-op when Sentry was never
// init'd because no DSN is set) and renders a calm, on-brand fallback instead
// of a blank white screen.
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    Sentry.captureException(error, { extra: { componentStack: info.componentStack } });
  }

  override render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-parchment px-6 text-center">
        <p className="font-sans text-xs uppercase tracking-[0.18em] text-ink/50">Something broke</p>
        <h1 className="mt-3 font-serif text-3xl text-ink sm:text-4xl">
          This page hit an unexpected error.
        </h1>
        <p className="mt-4 max-w-md font-sans text-sm text-ink/60">
          The fault has been logged. Reloading usually clears it — if it keeps happening, give it a
          few minutes and try again.
        </p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="mt-8 rounded-md bg-ink px-5 py-2 font-sans text-sm font-medium text-parchment hover:bg-oxblood"
        >
          Reload
        </button>
      </div>
    );
  }
}
